import { isNotAdminAreaLevel } from "@/helpers/datasetAttributeHelper";
import { formatDecimalNumber } from "@/helpers/numberHelpers";
import {
  DatasetFilter,
  EFilterOptionType,
  FilterOption
} from "@/types/dataset";

import dayjs from "dayjs";

import SavedFilterCard from "./SavedFilterCard";

interface SavedFilterItemProps {
  filter: DatasetFilter;
  filterOptions: FilterOption[];
  onEdit?: (filter: DatasetFilter) => void;
  onDelete?: (filter: DatasetFilter) => void;
}

/**
 * Renders the content for date/time/min-max filters
 */
const renderStaticFilterContent = (filter: DatasetFilter) => {
  if (filter.dateTimeRange) {
    const { from, to } = filter.dateTimeRange;
    return (
      <div className="text-xs text-txtPlaceholder font-normal">
        <div>
          From{" "}
          <span className="text-txtHeading font-semibold">
            {from.date && from.time
              ? dayjs(`${from.date} ${from.time}`).format("D MMM YYYY, HH:mm")
              : ""}
          </span>
          {","}
        </div>
        <div>
          Until{" "}
          <span className="text-txtHeading font-semibold">
            {to.date && to.time
              ? dayjs(`${to.date} ${to.time}`).format("D MMM YYYY, HH:mm")
              : ""}
          </span>
        </div>
      </div>
    );
  }

  if (filter.dateRange) {
    const { from, to } = filter.dateRange;
    return (
      <div className="text-xs text-txtPlaceholder font-normal">
        <div>
          From{" "}
          <span className="text-txtHeading font-semibold">
            {from ? dayjs(`${from}`).format("D MMM YYYY") : ""}
          </span>
          {","}
        </div>
        <div>
          Until{" "}
          <span className="text-txtHeading font-semibold">
            {to ? dayjs(`${to}`).format("D MMM YYYY") : ""}
          </span>
        </div>
      </div>
    );
  }

  if (filter.timeRange) {
    const { from, to } = filter.timeRange;
    return (
      <div className="text-xs text-txtPlaceholder font-normal">
        <div>
          From{" "}
          <span className="text-txtHeading font-semibold">{from ?? ""}</span>,
          Until{" "}
          <span className="text-txtHeading font-semibold">{to ?? ""}</span>
        </div>
      </div>
    );
  }

  if (filter.minMaxRange) {
    return (
      <div className="text-xs text-txtBody">
        <span>
          Min.{" "}
          <span className="text-txtHeading font-semibold">
            {formatDecimalNumber(filter.minMaxRange?.min)}
          </span>
          , Max.{" "}
          <span className="text-txtHeading font-semibold">
            {formatDecimalNumber(filter.minMaxRange?.max)}
          </span>
        </span>
      </div>
    );
  }

  return null;
};

/**
 * Renders the content for multiple selection filters
 */
const renderMultipleFilterContent = (filter: DatasetFilter) => {
  const count = filter.selectedOptions?.length ?? 0;
  return (
    <div className="text-xs text-txtPlaceholder">{count} Options Selected</div>
  );
};

const SavedFilterItem = ({
  filter,
  filterOptions,
  onEdit,
  onDelete
}: SavedFilterItemProps) => {
  // Skip admin area level filters
  if (!isNotAdminAreaLevel(filter.attributeCode)) {
    return null;
  }

  // Determine the title
  const title =
    filter.filterLabel ||
    filterOptions.find((o) => o.id === filter.filterId)?.label;

  // Render appropriate content based on filter type
  const content =
    filter.filterType === EFilterOptionType.MULTIPLE
      ? renderMultipleFilterContent(filter)
      : renderStaticFilterContent(filter);

  return (
    <SavedFilterCard
      filter={filter}
      title={title}
      content={content}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
};

export default SavedFilterItem;
